// ✅ src/pages/VehicleV2/BatteryCardReport_AllPrint.jsx
// This page:
// 1) reads filters from navigate("/vehicle/BatteryCardReport_AllPrint", { state: { filters } })
// 2) calls GET /api/batteryCardReport/list with those filters
// 3) renders the print table with real API data
//
// PRINT FIX:
//  - removed overflow-x-auto clipping while printing (.print-scroll)
//  - fixed table layout with <colgroup> percentages that add up to 100%
//  - min-widths are ignored in print so all 17 columns fit on A4 landscape
//  - page size is set only through @page (the old .print-landscape { size } was invalid)
//
// Backend expected:
//   app.use("/api/batteryCardReport", require("./routes/batteryCardReport"));
//
// List endpoint params expected by backend:
//   branchType, branch, fromDate, toDate,
//   batteryCode, batteryType, serialNo,
//   vehicleCode, vehicleName, vehicleRegNo,
//   batteryStatus, batteryMake, supplier,
//   vehicleMainType, status
//
// If your filter object keys are different, adjust mapFiltersToParams() below.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/batteryCardReport";

const toStr = (v) => String(v ?? "").trim();

function mapFiltersToParams(filters) {
  return {
    branchType: toStr(filters.branchType || "HO"),
    branch: toStr(filters.branch || "Select"),

    fromDate: toStr(filters.fromDate || ""),
    toDate: toStr(filters.toDate || ""),

    vehicleCode: toStr(filters.vehicleCode || ""),
    vehicleName: toStr(filters.vehicleName || ""),
    vehicleRegNo: toStr(filters.vehicleRegNo || filters.vehicleRegno || ""),

    batteryCode: toStr(filters.batteryCode || "Select"),
    batteryType: toStr(filters.batteryType || "Select"),
    serialNo: toStr(filters.serialNo || "Select"),

    batteryStatus: toStr(filters.batteryStatus || "Select"),
    batteryMake: toStr(filters.batteryMake || ""),

    supplier: toStr(filters.supplier || "Select"),

    // dropdown value (vehicletypeindex) or Select
    vehicleMainType: toStr(filters.vehicleMainType || "Select"),

    // ddl_status equivalent
    status: toStr(filters.status || "All"),
  };
}

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// One place to define every column: key, heading, print width (%), screen min-width (px), alignment.
// Print widths add up to 100 so the table always fits the page.
const COLUMNS = [
  { key: "sno",               label: "S No",               w: 3,  min: 30,  align: "center" },
  { key: "vehicleMainType",   label: "Vehicle Main Type",  w: 6,  min: 80,  align: "left" },
  { key: "vehicleCode",       label: "Vehicle Code",       w: 6,  min: 70,  align: "center" },
  { key: "vehicleName",       label: "Vehicle Name",       w: 6,  min: 80,  align: "left" },
  { key: "vehicleRegno",      label: "Vehicle Regno",      w: 6,  min: 80,  align: "left" },
  { key: "batteryCode",       label: "Battery Code",       w: 9,  min: 100, align: "left" },
  { key: "batterySerialNo",   label: "Battery Serial No",  w: 7,  min: 80,  align: "left" },
  { key: "batteryType",       label: "Battery Type",       w: 5,  min: 70,  align: "left" },
  { key: "batteryMake",       label: "Battery Make",       w: 5,  min: 70,  align: "left" },
  { key: "issuedBranch",      label: "Issued Branch",      w: 6,  min: 70,  align: "left" },
  { key: "currentBranch",     label: "Current Branch",     w: 6,  min: 70,  align: "left" },
  { key: "purchasedAmt",      label: "Purchased Amt",      w: 6,  min: 70,  align: "right" },
  { key: "issuedDate",        label: "Issued Date",        w: 6,  min: 70,  align: "center" },
  { key: "expectedLife",      label: "Expected Life",      w: 4,  min: 70,  align: "center" },
  { key: "warrantyPeriod",    label: "Warranty Period",    w: 5,  min: 70,  align: "center" },
  { key: "supplier",          label: "Supplier",           w: 6,  min: 50,  align: "center" },
  { key: "purposeOfPurchase", label: "Purpose of Purchase", w: 8, min: 120, align: "left" },
];

const alignClass = { left: "text-left", center: "text-center", right: "text-right" };

const BatteryCardMasterReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filters passed from listing page
  const filters = useMemo(() => location?.state?.filters || {}, [location]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [batteryData, setBatteryData] = useState([]);

  const params = useMemo(() => mapFiltersToParams(filters), [filters]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params });
        const rows = res?.data?.rows || [];

        // Normalize keys used by table
        const normalized = rows.map((r, i) => ({
          sno: i + 1,
          vehicleMainType: r.jobMainType ?? r.vehicleMainType ?? "",
          vehicleCode: r.vehicleCode ?? "",
          vehicleName: r.vehicleName ?? "",
          vehicleRegno: r.vehicleRegNo ?? r.vehicleRegno ?? "",
          batteryCode: r.batteryCode ?? "",
          batterySerialNo: r.batterySerialNumber ?? r.batterySerialNo ?? "",
          batteryType: r.batteryType ?? "",
          batteryMake: r.batteryMake ?? "",
          issuedBranch: r.issuedBranch ?? "",
          currentBranch: r.currentBranch ?? r.carriedBranch ?? "",
          purchasedAmt: fmtMoney(r.purchaseAmount ?? r.purchasedAmt ?? r.fld_purchaseamount),
          issuedDate: r.issueDate ?? r.issuedDate ?? "",
          expectedLife: r.expectedLife ?? "",
          warrantyPeriod: r.warrantyPeriod ?? "",
          supplier: r.supplier ?? "",
          purposeOfPurchase: r.purposeOfPurchase ?? r.purchasedFrom ?? r.purpose ?? "",
        }));

        if (alive) setBatteryData(normalized);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @page {
          size: A4 landscape;
          margin: 8mm;
        }

        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .no-print { display: none !important; }

          /* stop the wrapper from clipping / showing a scrollbar */
          .print-scroll {
            overflow: visible !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .print-table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            font-size: 7pt !important;
          }

          /* ignore the screen min-widths and let text wrap inside cells */
          .print-table th,
          .print-table td {
            min-width: 0 !important;
            padding: 2px !important;
            border: 1px solid #000 !important;
            white-space: normal !important;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .print-table thead { display: table-header-group; } /* repeat header on every page */
          .print-table tr { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      <div className="p-2 print:p-0">
        {/* Top actions (not printed) */}
        <div className="no-print flex items-center justify-between mb-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            ← Back
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Header Section */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Battery Card Master Report</h2>
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading report...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Main Table */}
        <div className="overflow-x-auto print-scroll">
          <table className="w-full border-collapse border-2 border-black text-xs print-table">
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: `${c.w}%` }} />
              ))}
            </colgroup>

            <thead>
              <tr className="bg-gray-200">
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className="border border-black px-1 py-1 font-bold text-center"
                    style={{ minWidth: `${c.min}px` }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {batteryData.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="border border-black px-2 py-2 text-center">
                    {loading ? "Loading..." : "No records found for selected filters."}
                  </td>
                </tr>
              ) : (
                batteryData.map((battery, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    {COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className={`border border-black px-1 py-1 ${alignClass[c.align]}`}
                      >
                        {battery[c.key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Print Button (bottom) */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg transition-colors duration-200"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatteryCardMasterReport;